import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import {
  api,
  CreateSubtaskDto,
  SubtaskDto,
  SubtaskListDto,
  SubtaskVersionChangelogList,
  UpdateSubtaskDto,
} from '@sib/api';
import { DEFAULT_PAGE, MAXIMUM_PAGINATION } from '@sib/api/constants';
import { QueryKey } from '@sib/api/enums';
import { TaskResourceField } from './task';

type ResourceRelation<
  T extends Record<string, string>,
  TKey extends string,
> = Record<Uppercase<TKey>, Record<string, `${TKey}.${T[keyof T]}`>>;

type SubtasksFieldsValue = keyof CreateSubtaskDto | keyof SubtaskDto;
type TaskRelation = ResourceRelation<typeof TaskResourceField, 'task'>;

export const SUBTASK_RESOURCE_NAME = 'subtask';

export const SubtaskResourceField = {
  TITLE: 'title',
  DESCRIPTION: 'description',
  PRIORITY_LEVEL: 'priorityLevel',
  COMPLETED: 'completed',
  CREATED_AT: 'createdAt',
  TASK_UUID: 'taskUuid',
  TASK: {
    UUID: 'task.uuid',
  },
} as const satisfies Record<string, SubtasksFieldsValue> | TaskRelation;

const CACHE_TIME = 5 * 1000;

export const useSubtaskList = (
  taskUuid: string,
  query?: Parameters<typeof api.tasks.subtaskControllerFindAll>[1],
  options?: UseQueryOptions<SubtaskListDto | null, Error, SubtaskListDto>
) =>
  useQuery({
    gcTime: CACHE_TIME,
    enabled: !!taskUuid,
    staleTime: CACHE_TIME,
    ...options,

    queryKey: [QueryKey.Resource.Subtask, QueryKey.Operation.List, taskUuid],
    queryFn: async () =>
      api.tasks.subtaskControllerFindAll(
        taskUuid,
        query || {
          page: DEFAULT_PAGE,
          size: MAXIMUM_PAGINATION,
        }
      ),
  });

export const useSubtaskEdit = (
  options?: UseMutationOptions<SubtaskDto, Error, UpdateSubtaskDto>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.Subtask, QueryKey.Operation.List],
      });

      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.Subtask,
          QueryKey.Operation.List,
          data.uuid,
        ],
      });

      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.Task, QueryKey.Operation.List],
      });

      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.Subtask,
          data.taskUuid,
          data.uuid,
          QueryKey.Resource.Changelogs,
        ],
      });

      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.Subtask, data.taskUuid, data.uuid],
      });

      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.TaskHistory,
          data.taskUuid,
          QueryKey.Resource.Changelogs,
        ],
      });

      options?.onSuccess?.(data, variables, context);
    },
    mutationFn: ({
      taskUuid,
      uuid,
      ...payload
    }: UpdateSubtaskDto & { taskUuid: string; uuid: string }) =>
      api.tasks.subtaskControllerUpdate(taskUuid, uuid, payload),
  });
};

export const useSubtaskCreate = (
  options?: UseMutationOptions<SubtaskDto, Error, CreateSubtaskDto>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.Subtask,
          QueryKey.Operation.List,
          variables.taskUuid,
        ],
      });

      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.Task, QueryKey.Operation.List],
      });

      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.TaskHistory,
          variables.taskUuid,
          QueryKey.Resource.Changelogs,
        ],
      });

      options?.onSuccess?.(data, variables, context);
    },
    mutationFn: ({
      taskUuid,
      ...payload
    }: CreateSubtaskDto & { taskUuid: string }) =>
      api.tasks.subtaskControllerCreate(taskUuid, { taskUuid, ...payload }),
  });
};

export const useSubtaskOne = (
  taskUuid: string,
  id?: string,
  options?: UseQueryOptions<SubtaskDto, Error, SubtaskDto>
) =>
  useQuery({
    ...options,
    enabled: !!taskUuid && !!id,
    queryKey: [QueryKey.Resource.Subtask, taskUuid, id],
    queryFn: async () => api.tasks.subtaskControllerFindOne(taskUuid, id || ''),
  });

export const useSubtaskDelete = (
  options?: UseMutationOptions<{}, Error, { taskUuid: string; id: string }>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.Subtask,
          QueryKey.Operation.List,
          variables.taskUuid,
        ],
      });

      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.Task, QueryKey.Operation.List],
      });

      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.TaskHistory,
          variables.taskUuid,
          QueryKey.Resource.Changelogs,
        ],
      });

      options?.onSuccess?.(data, variables, context);
    },

    mutationFn: ({ taskUuid, id }) =>
      api.tasks.subtaskControllerDeleteOne(taskUuid, id),
  });
};

export const useSubtaskChangeLog = (
  taskId: string,
  subtaskId?: string,
  options?: UseQueryOptions<
    SubtaskVersionChangelogList | null,
    Error,
    SubtaskVersionChangelogList
  >
) =>
  useQuery({
    ...options,
    enabled: !!taskId && !!subtaskId,
    queryKey: [
      QueryKey.Resource.Subtask,
      taskId,
      subtaskId,
      QueryKey.Resource.Changelogs,
    ],
    queryFn: () =>
      subtaskId
        ? api.tasks.subtaskVersionsControllerFindAllChangeLogs(
            taskId,
            subtaskId
          )
        : null,
  });
